import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const cls = "bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100";

function TopicForm({ slug }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get(`/blog/topics/${slug}`).then((r) => setT(r.data.topic));
  }, [slug]);
  if (!t) return null;
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/blog/topics/${slug}`, { title: t.title, description: t.description, intro: t.intro });
      setT(data.topic);
      toast.success(`Saved /blog/topic/${slug}`);
    } catch {
      toast.error("Could not save — check lengths (title 120, description 200).");
    }
  };
  const words = t.intro.trim().split(/\s+/).length;
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("image", f);
    try {
      const { data } = await api.post(`/admin/blog/topics/${slug}/cover`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setT(data.topic);
      toast.success("Cover replaced.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
    e.target.value = "";
  };
  return (
    <div className="border-t border-obsidian-600 pt-5" data-testid={`topic-admin-${slug}`}>
      <div className="flex items-center gap-4">
        {t.cover_url ? (
          <img src={`${BACKEND}${t.cover_url}`} alt="" className="h-16 w-28 object-cover border border-obsidian-600" data-testid={`topic-admin-cover-${slug}`} />
        ) : (
          <span className="h-16 w-28 border border-dashed border-obsidian-600 flex items-center justify-center font-sans text-[0.6rem] text-bone-500">generating…</span>
        )}
        <div className="flex flex-col gap-1">
          <a href={`/blog/topic/${slug}`} target="_blank" rel="noreferrer" className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta">/blog/topic/{slug} ↗</a>
          <label className="font-sans text-xs uppercase tracking-[0.2em] text-bone-400 hover:text-terracotta cursor-pointer">
            Replace cover
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`topic-admin-cover-input-${slug}`} />
          </label>
        </div>
      </div>
      <Input value={t.title} maxLength={120} onChange={(e) => setT({ ...t, title: e.target.value })} className={`${cls} mt-3 mb-2`} data-testid={`topic-admin-title-${slug}`} />
      <Input value={t.description} maxLength={200} onChange={(e) => setT({ ...t, description: e.target.value })} className={`${cls} mb-2`} data-testid={`topic-admin-desc-${slug}`} />
      <Textarea value={t.intro} maxLength={2000} onChange={(e) => setT({ ...t, intro: e.target.value })} className={`${cls} min-h-[140px] mb-2`} data-testid={`topic-admin-intro-${slug}`} />
      <div className="flex items-center gap-4">
        <Button onClick={save} data-testid={`topic-admin-save-${slug}`} className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 uppercase tracking-wider text-xs">Save</Button>
        <span className="font-sans text-xs text-bone-500">{words} words</span>
      </div>
    </div>
  );
}

export default function TopicAdmin() {
  const [topics, setTopics] = useState([]);
  useEffect(() => {
    api.get("/blog/topics").then((r) => setTopics(r.data.topics));
  }, []);
  return (
    <section className="border border-obsidian-600 p-6 mt-10 space-y-6" data-testid="topic-admin">
      <div>
        <h2 className="font-serif text-2xl text-bone-100 mb-2">Topic pages</h2>
        <p className="font-sans text-sm text-bone-400">Edit the intro, page title and Google description for each Field Notes topic page.</p>
      </div>
      {topics.map((t) => <TopicForm key={t.slug} slug={t.slug} />)}
    </section>
  );
}
